import {
  Box,
  Button,
  Modal,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getListUserWhenAdd } from "entities/users/service";
import EvaluationForm from "entities/evaluation_student/components/evaluation.form";

const modalBoxSx = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50%",
  bgcolor: "background.paper",
  boxShadow: 24,
  maxHeight: "70%",
  p: 4,
  borderRadius: 2,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  overflowY: "auto" as "auto",
};

/**
 * Mục "Thêm học sinh" dùng trong form tạo lớp học.
 * - Tìm & chọn học sinh (giống modal thêm học sinh ở trang /evaluation/:id).
 * - Gom học sinh vào state local (formData.students) để gửi kèm khi tạo lớp.
 * - Nút "Đánh giá" tái sử dụng EvaluationForm; chỉ lưu được sau khi lớp đã tồn tại
 *   (lúc tạo chưa có classId) — xem lưu ý trong index/service.
 */
const AddStudents = ({ formData, handleChangeCourse }) => {
  const selectedStudents: any[] = formData.students || [];
  // Sĩ số = "Số lượng người tham gia"; đủ thì không cho thêm nữa
  const limitMember = Number(formData.limit_member) || 0;
  const hasLimit = limitMember > 0;
  const isFull = hasLimit && selectedStudents.length >= limitMember;

  const [openAddMember, setOpenAddMember] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [listUser, setListUser] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<IntersectionObserver | null>(null);

  // Modal đánh giá cho từng học sinh
  const [evalStudent, setEvalStudent] = useState<any>(null);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackActive, setSnackActive] = useState(false);
  const toggleActive = useCallback(() => setSnackActive((a) => !a), []);

  const fetchUsers = async (newPage: number, search = "") => {
    try {
      const response = await getListUserWhenAdd(newPage, 10, search);
      const newUsers = response.data || [];
      setListUser((prev) => (newPage === 1 ? newUsers : [...prev, ...newUsers]));
      setHasMore(newUsers.length > 0);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  useEffect(() => {
    if (!openAddMember) return;
    fetchUsers(page, searchTerm);
  }, [openAddMember, page, searchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const lastUserRef = useCallback(
    (node: HTMLElement | null) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasMore]
  );

  const handleOpenAdd = () => {
    setSearchTerm("");
    setPage(1);
    setOpenAddMember(true);
  };

  const handleAddStudent = (user: any) => {
    if (isFull) {
      setOpenAddMember(false);
      return;
    }
    if (!selectedStudents.some((s) => s._id === user._id)) {
      handleChangeCourse("students", [...selectedStudents, user]);
    }
    setOpenAddMember(false);
  };

  const handleRemoveStudent = (id: string) => {
    handleChangeCourse(
      "students",
      selectedStudents.filter((s) => s._id !== id)
    );
  };

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "#fff",
        borderRadius: 4,
        boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
        mb: 2,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Học sinh{" "}
          {hasLimit && (
            <span style={{ color: isFull ? "#d32f2f" : "#888", fontWeight: 400 }}>
              ({selectedStudents.length}/{limitMember})
            </span>
          )}
        </Typography>
        <Button
          onClick={handleOpenAdd}
          disabled={!hasLimit || isFull}
          sx={{ textTransform: "none", color: "#2e7d32", fontWeight: 600 }}
        >
          + Thêm học sinh
        </Button>
      </Stack>
      {!hasLimit && (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Nhập "Số lượng người tham gia" trước khi thêm học sinh
        </Typography>
      )}
      {isFull && (
        <Typography variant="caption" sx={{ color: "#d32f2f" }} display="block" mb={1}>
          Lớp đã đủ số lượng người tham gia
        </Typography>
      )}

      {selectedStudents.length > 0 ? (
        <Stack gap={1}>
          {selectedStudents.map((student) => (
            <Box
              key={student._id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderWidth: "0px 0px 1px 0px",
                borderStyle: "solid",
                borderColor: "#e0dede",
                p: 1,
              }}
            >
              <Stack direction="row" gap={2} alignItems="center">
                <img
                  src={student.user_avatar || student.user_avatar_thumbnail}
                  alt=""
                  style={{ height: 44, width: 44, borderRadius: "50%" }}
                />
                <Stack>
                  <Typography variant="body1">
                    {student.display_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {student.user_email}
                  </Typography>
                </Stack>
              </Stack>
              <Box>
                <Button
                  onClick={() => setEvalStudent(student)}
                  sx={{ textTransform: "none", color: "#2e7d32" }}
                >
                  Đánh giá
                </Button>
                <Button
                  onClick={() => handleRemoveStudent(student._id)}
                  sx={{ textTransform: "none", color: "#d32f2f" }}
                >
                  Xoá
                </Button>
              </Box>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Chưa có học sinh nào trong lớp
        </Typography>
      )}

      {/* Modal tìm & thêm học sinh */}
      <Modal open={openAddMember} onClose={() => setOpenAddMember(false)}>
        <Box sx={modalBoxSx}>
          <Typography variant="h5">Thêm học sinh</Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Tìm kiếm theo tên..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ mb: 2 }}
          />
          {listUser.map((user, index) => {
            const isLastItem = listUser.length === index + 1;
            const alreadyAdded = selectedStudents.some(
              (s) => s._id === user._id
            );
            return (
              <Button
                key={user._id}
                ref={isLastItem ? lastUserRef : null}
                disabled={alreadyAdded}
                onClick={() => handleAddStudent(user)}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  width: "100%",
                  textTransform: "none",
                }}
              >
                <Stack direction="row" gap={2} alignItems="center">
                  <img
                    src={user?.user_avatar || user?.user_avatar_thumbnail}
                    alt=""
                    style={{ height: 50, width: 50, borderRadius: "50%" }}
                  />
                  <Stack
                    direction="column"
                    spacing={0.5}
                    alignItems="flex-start"
                  >
                    <Typography variant="body1" display="block">
                      {user.display_name}
                      {alreadyAdded ? " (đã thêm)" : ""}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      display="block"
                    >
                      {user.user_email}
                    </Typography>
                  </Stack>
                </Stack>
              </Button>
            );
          })}
          {!hasMore && <Typography>Không còn dữ liệu</Typography>}
        </Box>
      </Modal>

      {/* Modal đánh giá học sinh (tái sử dụng EvaluationForm) */}
      <Modal open={!!evalStudent} onClose={() => setEvalStudent(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "50%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <EvaluationForm
            studentId={evalStudent?._id}
            classId={formData.class_id}
            handleCloseModal={() => setEvalStudent(null)}
            setMessageSnackBar={setSnackMsg}
            toggleActive={toggleActive}
          />
        </Box>
      </Modal>

      <Snackbar
        open={snackActive}
        autoHideDuration={6000}
        onClose={toggleActive}
        message={snackMsg}
      />
    </Box>
  );
};

export default AddStudents;
